import React, { createContext, useContext, useEffect, useRef, useState } from "react";
import { motion, useInView } from "framer-motion";
import Lenis from "lenis";
import axios from "axios";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Button } from "../components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { COURSES } from "../config/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* ---------------- Smooth scroll (Lenis) ---------------- */
export function SmoothScroll() {
  useEffect(() => {
    const lenis = new Lenis({
      duration: 1.15,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      smoothWheel: true,
    });
    let raf;
    const loop = (time) => {
      lenis.raf(time);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);

    // If the page loads with a hash (e.g. arriving from another page at /#courses), scroll to it.
    if (window.location.hash && window.location.hash.length > 1) {
      const target = document.querySelector(window.location.hash);
      if (target) setTimeout(() => lenis.scrollTo(target, { offset: -80, immediate: false }), 300);
    }

    const onClick = (e) => {
      const a = e.target.closest('a[href^="#"]');
      if (!a) return;
      const id = a.getAttribute("href");
      if (id.length < 2) return;
      const el = document.querySelector(id);
      if (el) {
        e.preventDefault();
        lenis.scrollTo(el, { offset: -80 });
      }
    };
    document.addEventListener("click", onClick);
    return () => {
      cancelAnimationFrame(raf);
      document.removeEventListener("click", onClick);
      lenis.destroy();
    };
  }, []);
  return null;
}

/* ---------------- Reveal on scroll ---------------- */
export function Reveal({ children, delay = 0, y = 28, className = "", as = "div" }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });
  const MotionTag = motion[as] || motion.div;
  return (
    <MotionTag
      ref={ref}
      className={className}
      initial={{ opacity: 0, y }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.7, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </MotionTag>
  );
}

/* ---------------- Enquiry Dialog + context ---------------- */
const EnquiryContext = createContext(null);
export const useEnquiry = () => useContext(EnquiryContext);

export function EnquiryProvider({ children }) {
  const [open, setOpen] = useState(false);
  const [prefill, setPrefill] = useState({});

  const openEnquiry = (data = {}) => {
    setPrefill(data);
    setOpen(true);
  };

  return (
    <EnquiryContext.Provider value={{ openEnquiry }}>
      {children}
      <EnquiryDialog open={open} setOpen={setOpen} prefill={prefill} />
    </EnquiryContext.Provider>
  );
}

function EnquiryDialog({ open, setOpen, prefill }) {
  const [form, setForm] = useState({ name: "", phone: "", email: "", course: "", mode: "", message: "" });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) {
      setForm((f) => ({ ...f, course: prefill.course || "", mode: prefill.mode || "", message: prefill.message || "" }));
    }
  }, [open, prefill]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim()) {
      toast.error("Please share your name and phone number.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/enquiries`, form);
      toast.success("Thank you! AeroVaid will contact you shortly.");
      setOpen(false);
      setForm({ name: "", phone: "", email: "", course: "", mode: "", message: "" });
    } catch (err) {
      toast.error("Something went wrong. Please WhatsApp us instead.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="bg-slate-950 border-white/10 text-slate-100 sm:max-w-[480px]" data-testid="enquiry-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl tracking-tight">Book Your Free Demo</DialogTitle>
          <DialogDescription className="text-slate-400">
            Share your details and speak directly with the instructor. No commitment.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4 pt-2">
          <Input data-testid="enquiry-name" placeholder="Full name*" value={form.name} onChange={set("name")} className="bg-slate-900 border-white/10 h-12" />
          <Input data-testid="enquiry-phone" placeholder="Phone number*" value={form.phone} onChange={set("phone")} className="bg-slate-900 border-white/10 h-12" />
          <Input data-testid="enquiry-email" placeholder="Email (optional)" value={form.email} onChange={set("email")} className="bg-slate-900 border-white/10 h-12" />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Select value={form.course} onValueChange={(v) => setForm((f) => ({ ...f, course: v }))}>
              <SelectTrigger data-testid="enquiry-course" className="bg-slate-900 border-white/10 h-12"><SelectValue placeholder="Subject" /></SelectTrigger>
              <SelectContent className="bg-slate-950 border-white/10 text-slate-100">
                {COURSES.map((c) => (<SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>))}
                <SelectItem value="Not sure yet">Not sure yet</SelectItem>
              </SelectContent>
            </Select>
            <Select value={form.mode} onValueChange={(v) => setForm((f) => ({ ...f, mode: v }))}>
              <SelectTrigger data-testid="enquiry-mode" className="bg-slate-900 border-white/10 h-12"><SelectValue placeholder="Mode" /></SelectTrigger>
              <SelectContent className="bg-slate-950 border-white/10 text-slate-100">
                <SelectItem value="Online (Across India)">Online (Across India)</SelectItem>
                <SelectItem value="Offline (Mumbai)">Offline (Mumbai)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Textarea data-testid="enquiry-message" placeholder="Anything you'd like to tell us? (optional)" value={form.message} onChange={set("message")} className="bg-slate-900 border-white/10 min-h-[80px]" />
          <Button data-testid="enquiry-submit" type="submit" disabled={loading} className="w-full h-12 bg-white text-slate-950 hover:bg-slate-200 font-semibold rounded-sm">
            {loading ? "Sending..." : "Request Free Demo Class"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
